import { useState } from "react";
import { useFormContext } from "react-hook-form";
import { ClassNamesConfig, GroupBase } from "react-select";
import AsyncCreatable from "react-select/async-creatable";
import { z } from "zod";

import { Label } from "@/components/ui/label";
import {
  SearchIndicator,
  createClassNames,
} from "@/components/ui/react-select";
import { useOrganisation } from "@/features/select-organisation/api/get-my-organisation";
import { getUsers } from "@/features/users/api/get-users";
import { UserSelectOption } from "@/features/users/components/user-select-option";
import { cn } from "@/lib/utils";

import { AddSessionFormData } from "../types/add-session-schema";
import { UserOption } from "../types/user-option";

export function AttendeesSearchElement() {
  const form = useFormContext<AddSessionFormData>();
  const { organisation } = useOrganisation();
  const [value, setValue] = useState<string>("");

  const addAttendee = (attendee: UserOption) => {
    form.setValue("guests", [...(form.getValues("guests") || []), attendee]);
    form.trigger("guests");
  };

  const attendees = form.watch("guests");

  const guestsError = form.formState.errors.guests;
  const reactSelectClassNames = createClassNames({
    control: () => {
      return cn(!!guestsError && "border-destructive");
    },
  });

  const loadOptions = async (input: string): Promise<UserOption[]> => {
    const users = await getUsers({
      organisationId: organisation.id,
      pageSize: 20,
      pageIndex: 0,
      search: input,
    });
    const filteredUsers = users.items.filter(
      (user) => !attendees.some((attendee) => attendee.email === user.email),
    );

    return filteredUsers.map((user) => ({
      userId: user.id,
      firstName: user.firstName,
      lastName: user.lastName,
      label: `${user.firstName} ${user.lastName}`,
      email: user.email?.toLowerCase().trim() ?? "",
    }));
  };

  return (
    <div className="mb-4 space-y-4">
      <div className="space-y-2">
        <Label isRequired>Attendees</Label>
        <AsyncCreatable<UserOption>
          unstyled
          components={{
            DropdownIndicator: SearchIndicator,
            ClearIndicator: () => null,
            Option: UserSelectOption,
            IndicatorSeparator: () => null,
          }}
          ref={form.register("guests").ref}
          classNames={
            reactSelectClassNames as ClassNamesConfig<
              UserOption,
              false,
              GroupBase<UserOption>
            >
          }
          placeholder="Type attendee name..."
          formatCreateLabel={(input) => `Invite "${input}"`}
          noOptionsMessage={() => "Type attendee name or email..."}
          allowCreateWhileLoading={false}
          loadOptions={loadOptions}
          isValidNewOption={(option: string) => {
            const emailSchema = z.string().email();
            return emailSchema.safeParse(option).success;
          }}
          getNewOptionData={(input) => ({
            email: input,
            firstName: "",
            lastName: "",
            label: input,
            isExternal: true,
            isModerator: false,
          })}
          isDisabled={form.formState.isSubmitting}
          inputValue={value}
          onInputChange={(value) => {
            setValue(value);
          }}
          value={null}
          onChange={(value) => {
            if (value) {
              addAttendee(value);
              setValue(""); // Clear the input once added
              return null; // Prevent adding to internal state
            }
          }}
        />
        {guestsError && (
          <p className="text-sm font-medium text-destructive">
            {guestsError.message}
          </p>
        )}
        <p className="text-sm text-muted-foreground">
          This includes users of any organisations within the group
        </p>
      </div>
    </div>
  );
}
